import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

interface AlertDialogOptions {
  title: string;
  description?: string;
  confirmText?: string;
  cancelText?: string;
  variant?: 'default' | 'destructive';
}

interface AlertDialogContextValue {
  confirm: (options: AlertDialogOptions) => Promise<boolean>;
}

const AlertDialogContext = createContext<AlertDialogContextValue | null>(null);

export const useAlertDialog = () => {
  const context = useContext(AlertDialogContext);
  if (!context) {
    throw new Error('useAlertDialog must be used within AlertDialogProvider');
  }
  return context;
};

export const AlertDialogProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [open, setOpen] = useState(false);
  const [options, setOptions] = useState<AlertDialogOptions | null>(null);
  const [resolvePromise, setResolvePromise] = useState<
    ((value: boolean) => void) | null
  >(null);
  const clearOptionsTimeoutRef = useRef<number | null>(null);

  const confirm = useCallback((opts: AlertDialogOptions): Promise<boolean> => {
    return new Promise((resolve) => {
      // Clear any pending timeout
      if (clearOptionsTimeoutRef.current) {
        clearTimeout(clearOptionsTimeoutRef.current);
        clearOptionsTimeoutRef.current = null;
      }
      setOptions(opts);
      setResolvePromise(() => resolve);
      setOpen(true);
    });
  }, []);

  const clearOptionsAfterAnimation = useCallback(() => {
    // Clear any existing timeout
    if (clearOptionsTimeoutRef.current) {
      clearTimeout(clearOptionsTimeoutRef.current);
    }
    // Clear options after animation duration (200ms from className)
    clearOptionsTimeoutRef.current = window.setTimeout(() => {
      setOptions(null);
      clearOptionsTimeoutRef.current = null;
    }, 200);
  }, []);

  const handleConfirm = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(true);
      setResolvePromise(null);
    }
    setOpen(false);
    clearOptionsAfterAnimation();
  }, [resolvePromise, clearOptionsAfterAnimation]);

  const handleCancel = useCallback(() => {
    if (resolvePromise) {
      resolvePromise(false);
      setResolvePromise(null);
    }
    setOpen(false);
    clearOptionsAfterAnimation();
  }, [resolvePromise, clearOptionsAfterAnimation]);

  const handleOpenChange = useCallback(
    (isOpen: boolean) => {
      if (!isOpen) {
        // Dialog is fully closed, resolve promise if it hasn't been resolved yet
        // (e.g., if closed via ESC key)
        if (resolvePromise) {
          resolvePromise(false);
          setResolvePromise(null);
        }
        // Clear options after animation completes
        clearOptionsAfterAnimation();
      }
    },
    [resolvePromise, clearOptionsAfterAnimation],
  );

  // Cleanup timeout on unmount
  useEffect(() => {
    return () => {
      if (clearOptionsTimeoutRef.current) {
        clearTimeout(clearOptionsTimeoutRef.current);
      }
    };
  }, []);

  return (
    <AlertDialogContext.Provider value={{ confirm }}>
      {children}
      <AlertDialog open={open} onOpenChange={handleOpenChange}>
        <AlertDialogContent onOverlayClick={handleCancel}>
          <AlertDialogHeader>
            <AlertDialogTitle>{options?.title}</AlertDialogTitle>
            {options?.description && (
              <AlertDialogDescription>
                {options.description}
              </AlertDialogDescription>
            )}
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel onClick={handleCancel}>
              {options?.cancelText || 'Cancel'}
            </AlertDialogCancel>
            <AlertDialogAction onClick={handleConfirm}>
              {options?.confirmText || 'Confirm'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </AlertDialogContext.Provider>
  );
};
